import styled from 'styled-components'
import { Card } from 'react-bootstrap'

export const StatsSection = styled.div`
  width: 100%;
  min-height: 700px;
  height: 100%;
  display: flex;
  flex-direction: column;
`

export const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
`

export const DetailCard = styled(Card)`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 12px;
  background-color: #2d2d2d;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;

  .card-body {
    padding: 1.5rem;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: 768px) {
      padding: 1rem;
    }
  }
`

export const DetailHeader = styled.div`
  background: linear-gradient(45deg, #402563, #2042ba);
  padding: 16px 24px;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: 12px 16px;
  }

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;

    @media (max-width: 576px) {
      font-size: 16px;
    }
  }
`

export const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  canvas {
    width: 100% !important;
    height: 100% !important;
    max-height: 500px;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(45, 45, 45, 0.7);
    border-radius: 8px;
  }
`

export const ViewContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  overflow: hidden;
`

export const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 1.5rem 1.5rem;
  min-height: 0;

  @media (max-width: 768px) {
    padding: 0.5rem 0.5rem 0.5rem;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

export const PageHeader = styled(Card)`
  margin: 0;
  background: #1a1a1a !important;
  border: none !important;
  padding: 0.5rem;

  .card-header {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    background: transparent;
    padding: 0;
  }

  .subtitle {
    color: #8c8c8c;
    font-size: 0.7rem;
    margin-top: 0.25rem;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.5rem 0;
  }
`

export const FlipCard = styled.div`
  perspective: 1000px;
  height: calc(100% - 44px); // Subtract the height of SectionTitle

  &.flip-card {
    .flip-card-inner {
      transform-style: preserve-3d;
      transition: transform 0.6s;
      position: relative;
      width: 100%;
      height: 100%;
    }

    &.flipped .flip-card-inner {
      transform: rotateY(180deg);
    }
  }

  .flip-card-front,
  .flip-card-back {
    position: absolute;
    width: 100%;
    height: 100%;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    background-color: #2d2d2d;
    border-radius: 12px;
    transform-style: preserve-3d;
    overflow: hidden;

    > * {
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
    }

    ${DetailCard} {
      border-radius: 12px;
    }
  }

  .flip-card-front {
    transform: rotateY(0deg);
  }

  .flip-card-back {
    transform: rotateY(180deg);
  }

  .class-list-container {
    height: 450px;
    overflow-y: auto;
    margin-top: 1rem;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }

  .filters-section {
    padding: 0 0 1rem 0;
  }

  .class-table {
    margin: 0;

    th,
    td {
      border-color: rgba(255, 255, 255, 0.1);
    }
  }
`

export const StatCard = styled(Card)<{
  $variant?: 'primary' | 'info' | 'success' | 'warning' | 'purple'
}>`
  height: 100%;
  border: none;
  border-radius: 12px;
  background-color: #2d2d2d;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(45, 45, 45, 0.7);
    z-index: 1;
  }

  .card-body {
    background: ${(props) => {
      switch (props.$variant) {
        case 'primary':
          return 'linear-gradient(45deg, #0d47a1, #1976d2)'
        case 'info':
          return 'linear-gradient(45deg, #006064, #00acc1)'
        case 'success':
          return 'linear-gradient(45deg, #1b5e20, #43a047)'
        case 'warning':
          return 'linear-gradient(45deg, #e65100, #fb8c00)'
        case 'purple':
          return 'linear-gradient(45deg, #402563, #2042ba)'
        default:
          return 'linear-gradient(45deg, #402563, #2042ba)'
      }
    }};
    border-radius: 12px;
    padding: 1.5rem;

    @media (max-width: 768px) {
      padding: 1rem;
    }
  }
`

export const StatContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 576px) {
    gap: 12px;
  }
`

export const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  font-size: 24px;
  background: rgba(255, 255, 255, 0.1);

  @media (max-width: 576px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`

export const StatInfo = styled.div`
  flex: 1;

  h3 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #ffffff;

    @media (max-width: 576px) {
      font-size: 20px;
    }
  }

  p {
    margin: 4px 0 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;

    @media (max-width: 576px) {
      font-size: 12px;
    }
  }
`
